import React, { createContext, useContext, useState, useCallback } from 'react'
import { useToast } from '../hooks/useToast'
import { ConfirmationDialog } from '../components/ui/confirmation-dialog'

interface NotificationContextType {
  showToast: (message: string, type?: 'success' | 'error' | 'warning' | 'info', title?: string) => void
  showConfirmation: (config: ConfirmationConfig) => Promise<boolean>
}

interface ConfirmationConfig {
  title: string
  description?: string
  variant?: 'default' | 'destructive' | 'warning' | 'success' | 'info'
  confirmText?: string
  cancelText?: string
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

export const useNotifications = () => {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  return context
}

interface NotificationProviderProps {
  children: React.ReactNode
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const { toast } = useToast()
  const [confirmationConfig, setConfirmationConfig] = useState<ConfirmationConfig | null>(null)
  const [confirmationResolve, setConfirmationResolve] = useState<((result: boolean) => void) | null>(null)

  const showToast = useCallback((message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info', title?: string) => {
    const variant = type === 'error' ? 'destructive' : type
    
    toast({
      title: title || (type === 'success' ? 'Success' : type === 'error' ? 'Error' : type === 'warning' ? 'Warning' : 'Info'),
      description: message,
      variant,
      duration: type === 'error' ? 5000 : 3000,
    })
  }, [toast])

  const showConfirmation = useCallback((config: ConfirmationConfig): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfirmationConfig(config)
      setConfirmationResolve(() => resolve)
    })
  }, [])

  const handleConfirm = useCallback(() => {
    if (confirmationResolve) {
      confirmationResolve(true)
      setConfirmationResolve(null)
    }
    setConfirmationConfig(null)
  }, [confirmationResolve])

  const handleCancel = useCallback(() => {
    if (confirmationResolve) {
      confirmationResolve(false)
      setConfirmationResolve(null)
    }
    setConfirmationConfig(null)
  }, [confirmationResolve])

  return (
    <NotificationContext.Provider value={{ showToast, showConfirmation }}>
      {children}
      {confirmationConfig && (
        <ConfirmationDialog
          open={!!confirmationConfig}
          onOpenChange={(open) => !open && handleCancel()}
          title={confirmationConfig.title}
          description={confirmationConfig.description}
          variant={confirmationConfig.variant || 'default'}
          confirmText={confirmationConfig.confirmText}
          cancelText={confirmationConfig.cancelText}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </NotificationContext.Provider>
  )
}









